import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import CommentComposer from "@/components/CommentComposer";
import CommentItem from "@/components/CommentItem";
import SummarizeButton from "@/components/SummarizeButton";
import { MessageSquare } from "lucide-react";

/**
 * Comments hub for a task. Renders a header, discreet AI summary trigger,
 * an inline composer (default = Komentar), and the thread of top-level
 * comments with their replies nested underneath.
 *
 * NO chat bubbles: each entry is a rectangular "record" with a colored
 * left border indicating type (ochre for Pitanje, forest for Odluka /
 * Odobrenje, neutral for Komentar).
 */
export default function TaskCommentsSection({ taskId, contactsById = {}, onMutated }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map user_id → { name } via internal_user contacts
  const userMap = useMemo(() => {
    const m = {};
    for (const c of Object.values(contactsById)) {
      if (c.kind === "internal_user" && c.linked_user_id) {
        m[c.linked_user_id] = { name: c.name };
      }
    }
    return m;
  }, [contactsById]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/tasks/${taskId}/comments`);
      setComments(data || []);
    } catch (e) {
      setError("Nismo uspeli da učitamo razgovor. Osveži za novi pokušaj.");
    }
    setLoading(false);
  };

  // Wrap child change → refresh local + notify parent (Dashboard chip)
  const handleChanged = async () => {
    await load();
    onMutated?.();
  };

  useEffect(() => { if (taskId) load(); }, [taskId]);

  const { roots, byParent } = useMemo(() => {
    const roots = [];
    const byParent = {};
    for (const c of comments) {
      if (c.parent_comment_id) {
        (byParent[c.parent_comment_id] = byParent[c.parent_comment_id] || []).push(c);
      } else {
        roots.push(c);
      }
    }
    return { roots, byParent };
  }, [comments]);

  return (
    <div className="space-y-2.5" data-testid="task-comments-section">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider font-semibold text-ink-muted inline-flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" /> Razgovor
          {comments.length > 0 && (
            <span className="text-ink-muted normal-case font-normal">
              · {comments.length} {comments.length === 1 ? "upis" : "upisa"}
            </span>
          )}
        </h3>
        <SummarizeButton taskId={taskId} commentsCount={comments.length} />
      </div>

      <CommentComposer taskId={taskId} onPosted={handleChanged} />

      {loading ? (
        <div className="text-xs text-ink-muted flex items-center gap-2" data-testid="comments-loading">
          <span className="inline-block w-3 h-3 border-2 border-ink-muted/40 border-t-forest rounded-full animate-spin" />
          Učitavam razgovor…
        </div>
      ) : error ? (
        <div
          data-testid="comments-error"
          className="text-xs text-terracotta border border-terracotta/30 bg-terracotta/6 rounded-md px-3 py-2 flex items-center justify-between gap-2"
        >
          <span>{error}</span>
          <button
            onClick={load}
            className="text-terracotta font-medium hover:underline"
            data-testid="comments-error-retry"
          >
            Osveži
          </button>
        </div>
      ) : roots.length === 0 ? (
        <div
          data-testid="comments-empty"
          className="text-xs text-ink-muted border border-dashed border-line rounded-md px-3 py-3 text-center"
        >
          Još nema upisa. Prvi komentar, pitanje ili odluka ide gore.
        </div>
      ) : (
        <div className="space-y-2">
          {roots.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              replies={byParent[c.id] || []}
              userMap={userMap}
              taskId={taskId}
              onChanged={handleChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}
